'use client';
import { motion } from 'motion/react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Send,
  MessageCircle,
  BarChart3,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-white py-24 sm:py-32"
      aria-labelledby="hiw-heading"
    >
      {/* Subtle warm wash */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at 80% 80%, hsl(30 95% 52% / 0.03), transparent 50%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          className="max-w-2xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            How It Works
          </p>
          <h2
            id="hiw-heading"
            className="mt-4 font-serif text-3xl font-700 leading-tight text-foreground sm:text-4xl md:text-5xl"
          >
            From data to decisions
            <br />
            in three steps.
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="mt-16 space-y-6 sm:mt-20">
          {/* Step 01 — Upload */}
          <motion.div
            className="overflow-hidden rounded-2xl border border-border bg-background"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
            variants={fadeUp}
          >
            <div className="flex flex-col gap-8 p-6 sm:p-8 md:flex-row md:items-center md:gap-12 md:p-10">
              {/* Text */}
              <div className="flex-1">
                <span className="font-serif text-5xl font-700 text-accent/25 sm:text-6xl">
                  01
                </span>
                <h3 className="mt-3 text-xl font-semibold text-foreground sm:text-2xl">
                  Upload your DMS data
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Export from your existing system. Our importer auto-maps
                  columns, deduplicates entities, and builds your territory
                  hierarchy — all from a single CSV.
                </p>
              </div>

              {/* Mini visual: CSV upload */}
              <div
                className="w-full shrink-0 md:w-[320px] lg:w-[380px]"
                aria-hidden="true"
              >
                <div className="rounded-xl border border-border bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-semibold text-foreground">
                      Data Import
                    </span>
                  </div>
                  {/* Drop zone */}
                  <div className="flex flex-col items-center rounded-lg border-2 border-dashed border-border px-4 py-6 text-center">
                    <FileSpreadsheet className="mb-2 h-8 w-8 text-accent/60" />
                    <p className="text-xs font-medium text-foreground">
                      orders_march_2026.csv
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      4,811 rows &middot; 2.3 MB
                    </p>
                  </div>
                  {/* Progress */}
                  <div className="mt-3 space-y-1.5">
                    {[
                      { label: 'Columns mapped', done: true },
                      { label: '45 salesmen created', done: true },
                      { label: '892 retailers matched', done: true },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="flex items-center gap-2 text-[10px]"
                      >
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        <span className="text-foreground">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Step 02 — Intelligence */}
          <motion.div
            className="overflow-hidden rounded-2xl border border-border bg-background"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
            variants={fadeUp}
          >
            <div className="flex flex-col gap-8 p-6 sm:p-8 md:flex-row-reverse md:items-center md:gap-12 md:p-10">
              <div className="flex-1">
                <span className="font-serif text-5xl font-700 text-accent/25 sm:text-6xl">
                  02
                </span>
                <h3 className="mt-3 text-xl font-semibold text-foreground sm:text-2xl">
                  Get actionable intelligence
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Our engine computes daily snapshots, detects anomalies, scores
                  revenue opportunities, and surfaces signals — ready before
                  your morning chai.
                </p>
              </div>

              {/* Mini visual: Alerts + metrics */}
              <div
                className="w-full shrink-0 md:w-[320px] lg:w-[380px]"
                aria-hidden="true"
              >
                <div className="rounded-xl border border-border bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-semibold text-foreground">
                      Intelligence Engine
                    </span>
                  </div>

                  {/* Alert */}
                  <div className="mb-3 rounded-lg border border-amber-200/60 bg-amber-50/60 p-2.5">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3 text-amber-600" />
                      <span className="text-[10px] font-semibold text-amber-900">
                        Coverage alert: 3 salesmen below threshold
                      </span>
                    </div>
                  </div>

                  {/* Mini metrics */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      {
                        label: 'Anomalies',
                        value: '7',
                        sub: 'auto-detected',
                      },
                      {
                        label: 'Opportunities',
                        value: '₹18.5L',
                        sub: 'revenue gap',
                      },
                      {
                        label: 'Dormant',
                        value: '142',
                        sub: 'retailers',
                      },
                      {
                        label: 'Pace',
                        value: '0.87x',
                        sub: 'vs target',
                      },
                    ].map((m) => (
                      <div
                        key={m.label}
                        className="rounded-lg bg-[hsl(40,30%,98%)] p-2.5"
                      >
                        <p className="text-[9px] text-muted-foreground">
                          {m.label}
                        </p>
                        <p className="text-sm font-bold text-foreground">
                          {m.value}
                        </p>
                        <p className="text-[8px] text-muted-foreground">
                          {m.sub}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Step 03 — Act */}
          <motion.div
            className="overflow-hidden rounded-2xl border border-border bg-background"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
            variants={fadeUp}
          >
            <div className="flex flex-col gap-8 p-6 sm:p-8 md:flex-row md:items-center md:gap-12 md:p-10">
              <div className="flex-1">
                <span className="font-serif text-5xl font-700 text-accent/25 sm:text-6xl">
                  03
                </span>
                <h3 className="mt-3 text-xl font-semibold text-foreground sm:text-2xl">
                  Act and measure
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Create targeted schemes, send via WhatsApp, and track delivery
                  and conversion. Every action has a measurable ROI. Every crore
                  accounted for.
                </p>
              </div>

              {/* Mini visual: WhatsApp + ROI */}
              <div
                className="w-full shrink-0 md:w-[320px] lg:w-[380px]"
                aria-hidden="true"
              >
                <div className="rounded-xl border border-border bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <Send className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-semibold text-foreground">
                      Campaign Results
                    </span>
                  </div>

                  {/* Funnel */}
                  <div className="mb-3 space-y-1">
                    {[
                      { label: 'Sent', val: '142', pct: '100%', w: '100%' },
                      {
                        label: 'Delivered',
                        val: '131',
                        pct: '92%',
                        w: '92%',
                      },
                      { label: 'Read', val: '111', pct: '78%', w: '78%' },
                      {
                        label: 'Responded',
                        val: '48',
                        pct: '34%',
                        w: '34%',
                      },
                    ].map((f) => (
                      <div key={f.label} className="flex items-center gap-2">
                        <span className="w-16 text-[9px] text-muted-foreground">
                          {f.label}
                        </span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full rounded-full bg-emerald-500/70"
                            style={{ width: f.w }}
                          />
                        </div>
                        <span className="w-8 text-right font-mono text-[9px] font-semibold text-foreground">
                          {f.val}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* ROI card */}
                  <div className="flex items-center justify-between rounded-lg bg-accent/8 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-3.5 w-3.5 text-accent" />
                      <span className="text-[10px] font-semibold text-foreground">
                        Campaign ROI
                      </span>
                    </div>
                    <span className="font-serif text-lg font-700 text-accent">
                      14.2x
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-1.5">
                    <MessageCircle className="h-3 w-3 text-emerald-600" />
                    <span className="text-[9px] text-muted-foreground">
                      Sent via WhatsApp &middot; ₹71 total cost
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
